import React, { FC, useState } from 'react'
import firebase from 'firebase/app'
import * as Sentry from '@sentry/react'
import { CheckIcon, XIcon } from '@heroicons/react/outline'
import { InputDialog, LabelWrapper, Spinner } from '../../System'
import { useNotifications } from '../../Notifications'
import { StyledPassword } from '..'

const Password: FC = () => {
  const { addNotification } = useNotifications()

  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false)

  const cancelEdit = () => {
    setPassword('')
    setNewPassword('')
    setIsChangingPassword(false)
  }

  const handleSubmit = async () => {
    try {
      setLoading(true)

      if (!password.trim()) {
        return setShowPasswordConfirmation(true)
      }

      if (!newPassword.trim()) {
        return
      }

      const profile = (await firebase.auth().currentUser) as firebase.User

      if (!profile?.email) {
        throw new Error(
          'Error with currently logged in user. Please refresh your page and try again.'
        )
      }

      const credential = firebase.auth.EmailAuthProvider.credential(profile.email, password)
      await profile.reauthenticateWithCredential(credential)
      await profile.updatePassword(newPassword)

      addNotification({
        message: 'Password Changed!',
        type: 'success',
        interval: 5000
      })

      setPassword('')
      setNewPassword('')
      setIsChangingPassword(false)
    }
    catch (err) {
      addNotification({
        message: (err as any)?.message || 'There was a problem updating your password',
        type: 'error'
      })
      Sentry.captureException(err)
    }
    finally {
      setLoading(false)
    }
  }

  return (
    <>
      <LabelWrapper label="Password">
        <StyledPassword>
          {!isChangingPassword && (
            <button
              type="button"
              className="change-password-btn"
              onClick={() => setIsChangingPassword(true)}
            >
              <span>Change Password</span>
            </button>
          )}
          {isChangingPassword && (
            <div className="change-password-input">
              <input
                value={newPassword}
                type="password"
                placeholder="New Password"
                autoFocus
                required
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button type="button" disabled={loading} onClick={() => cancelEdit()}>
                <XIcon />
              </button>
              <button
                type="button"
                className="save-btn"
                disabled={loading}
                onClick={() => handleSubmit()}
              >
                {loading ? <Spinner theme="light" size="small" /> : <CheckIcon />}
              </button>
            </div>
          )}
        </StyledPassword>
      </LabelWrapper>
      <InputDialog
        isOpen={showPasswordConfirmation}
        onConfirm={() => handleSubmit()}
        title="Current Password"
        value={password}
        inputProps={{ type: 'password' }}
        confirmButtonText="Update Password"
        onChange={(value) => setPassword(value)}
        onClose={() => setShowPasswordConfirmation(false)}
      />
    </>
  )
}

export default Password
